import type {
  ConvertibleUnit,
  FormatOptions,
  Unit,
} from '@dynatrace-sdk/units';

import { accessValue } from '../../../core/utils/accessors/access-value.js';
import type { InverseTrendOptions } from '../../core/components/single-value-renderer/components/trend/types/inverse-trend-options.js';
import type { SingleValueUnit } from '../../core/components/single-value-renderer/types/single-value-base-props.js';
import type { Formatter } from '../../core/types/formatter.js';
import type {
  PartialSingleValueGridConfig,
  SingleValueGridAccessorsConfig,
} from '../types/single-value-grid.js';

/**
 * Retrieves configuration from the provided object using accessors specified in the accessors' configuration.
 *
 * @param data - The object containing the data.
 * @param accessors - The configuration specifying the accessors.
 * @returns The accessed configuration object.
 */
export function getConfigFromAccessors(
  data: Record<string, unknown>,
  accessors: SingleValueGridAccessorsConfig,
): PartialSingleValueGridConfig {
  const accessedConfig: PartialSingleValueGridConfig = {};

  if (accessors.colorAccessor) {
    accessedConfig.color = accessValue<string>(data, accessors.colorAccessor);
  }

  if (accessors.value) {
    accessedConfig.value = {};

    if (accessors.value.formatterAccessor) {
      accessedConfig.value.formatter = accessValue<
        Formatter | FormatOptions<Unit, ConvertibleUnit>
      >(data, accessors.value.formatterAccessor);
    }

    if (accessors.value.unitAccessor) {
      // oxlint-disable-next-line typescript/no-deprecated
      accessedConfig.value.unit = accessValue<SingleValueUnit>(
        data,
        accessors.value.unitAccessor,
      );
    }
  }

  if (accessors.trend) {
    accessedConfig.trend = {};

    if (accessors.trend.formatterAccessor) {
      accessedConfig.trend.formatter = accessValue<
        Formatter | FormatOptions<Unit, ConvertibleUnit>
      >(data, accessors.trend.formatterAccessor);
    }

    if (accessors.trend.inverseTrendAccessor) {
      accessedConfig.trend.inverseTrend = accessValue<InverseTrendOptions>(
        data,
        accessors.trend.inverseTrendAccessor,
      );
    }
  }

  if (accessors.sparkline) {
    accessedConfig.sparkline = {};

    if (accessors.sparkline.colorAccessor) {
      accessedConfig.sparkline.color = accessValue<string>(
        data,
        accessors.sparkline.colorAccessor,
      );
    }
  }

  return accessedConfig;
}
